/* eslint-disable max-lines */
import { FC, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import AddIcon from "../../../../components/shared/svgs/Add";
import { ILocation } from "../../../../interfaces/endpoints/ILocations";
import { ITripType } from "../../../../interfaces/endpoints/trips/Trips.types";
import { cx } from "../../../../utils/classnames";
import CreateTripWrapper from "./CreateTripWrapper";
import EmptyLineTrip from "./EmptyLineTrip";

interface SchedulesProps {
  locations: ILocation[];
  schedulesRes?: ITripType["schedules"];
  tripIndex?: number;
}

// The fields a stop row owns. Two rows change places by swapping these, so the stop order the
// form posts (its index order) is the order the operator sees.
const STOP_FIELDS = [
  "stationId", "duration", "departureTimeString", "arrivalTimeString", "tripId", "id"
] as const;

// How long a row that changed place stays marked; matches the stop-moved animations in index.css.
const MOVE_MARK_MS = 700;

const Schedules: FC<SchedulesProps> = ({
  locations,
  schedulesRes,
  tripIndex
}) => {
  // The rows, each the form index it owns (schedules.<index>.*), kept in index order because that
  // is the order the stops are posted in, and the server stores stops in posted order.
  const [rows, setRows] = useState<number[]>([]);
  // Rows whose stop just changed place, and which way it went, for the slide animation.
  const [moved, setMoved] = useState<Record<number, "up" | "down">>({});
  const { unregister, getValues, setValue, trigger } = useFormContext<ITripType>();

  useEffect(() => {
    setRows(schedulesRes?.map((_, i) => i) ?? []);
  }, [schedulesRes]);

  const handleDeletePathStop = useCallback((id: number) => {
    unregister(`schedules.${id}`);
    setRows((prev) => prev.filter((x) => x !== id));
  }, [unregister]);

  // A new row takes the next free index, never one a deleted row used to own: reusing it would
  // land the new stop on the deleted stop's registered fields.
  const handleNewPathStop = () => {
    setRows((prev) => [...prev, prev.length ? Math.max(...prev) + 1 : 0]);
  };

  // After a stop's distance changes, it bubbles past its neighbours until the rows read in travel
  // order: the shorter distance moves up, the longer moves down, and both rows slide so the
  // operator sees the stop change place. Rows with no distance yet stay where they are.
  const handleOffsetChange = useCallback((index: number) => {
    const offsetOf = (i: number) => {
      const value = Number(getValues(`schedules.${i}.duration`));
      return value > 0 ? value : null;
    };
    const swap = (a: number, b: number) => {
      STOP_FIELDS.forEach((field) => {
        const first = getValues(`schedules.${a}.${field}`);
        const second = getValues(`schedules.${b}.${field}`);
        setValue(`schedules.${a}.${field}`, second, { shouldValidate: true });
        setValue(`schedules.${b}.${field}`, first, { shouldValidate: true });
      });
    };

    const marks: Record<number, "up" | "down"> = {};
    let pos = rows.indexOf(index);
    const mine = offsetOf(index);
    if (pos < 0 || mine === null) return;
    // Up, while the row above is further along the trip than this stop.
    while (pos > 0) {
      const above = rows[pos - 1];
      const theirs = offsetOf(above);
      if (theirs === null || theirs <= mine) break;
      swap(above, rows[pos]);
      marks[above] = "up";
      marks[rows[pos]] = "down";
      pos -= 1;
    }
    // Or down, while the row below is earlier along the trip than this stop.
    while (pos < rows.length - 1) {
      const below = rows[pos + 1];
      const theirs = offsetOf(below);
      if (theirs === null || theirs >= mine) break;
      swap(below, rows[pos]);
      marks[below] = "down";
      marks[rows[pos]] = "up";
      pos += 1;
    }
    // The distance rules (before the arrival, no duplicates) read every row, so every row's
    // message is refreshed, not only the one just typed in.
    void trigger(rows.map((i) => `schedules.${i}.duration` as const));
    if (Object.keys(marks).length === 0) return;
    setMoved(marks);
    window.setTimeout(() => setMoved({}), MOVE_MARK_MS);
  }, [rows, getValues, setValue, trigger]);

  // A station picked on one row can clear or raise the duplicate message on another.
  const handleStationChange = useCallback(() => {
    void trigger(rows.map((i) => `schedules.${i}.stationId` as const));
  }, [rows, trigger]);

  const stationOptions = locations?.map((lc) => ({
    id: lc.id,
    name: lc.name + `
               - ${lc.stationtype === 1 ? "Bus" : "Tren"}`
  }));

  return (
    <CreateTripWrapper title="Ndalesat" className="py-5">
      <button
        type="button"
        className="absolute right-0 top-0 p-5 text-primary flex items-center
        transitions hover:font-semibold cursor-pointer transition-all duration-75"
        onClick={handleNewPathStop}
      >
        <span className="pr-2">
          <AddIcon size={16} />
        </span>
        Shto ndalese të re
      </button>
      {rows.length === 0 ? (
        <div
          className="w-full bg-gray-100 h-14
  rounded-md justify-center"
        >
          <p className="text-gray-500 text-center p-4">
            Ende nuk keni shtuar ndalesa...
          </p>
        </div>
      ) : (
        <ol className="pl-4 text-sm list-decimal">
          {rows.map((id) => (
            <li
              key={id}
              className={cx([
                "px-2 rounded-md",
                moved[id] === "up" && "stop-moved-up",
                moved[id] === "down" && "stop-moved-down"
              ])}
            >
              <EmptyLineTrip
                handleDelete={handleDeletePathStop}
                options={stationOptions}
                index={id}
                tripIndex={tripIndex}
                onOffsetChange={handleOffsetChange}
                onStationChange={handleStationChange}
              />
            </li>
          ))}
        </ol>
      )}
    </CreateTripWrapper>
  );
};

export default Schedules;
